import React, { useEffect, useState, useMemo } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Polygon,
} from "@react-google-maps/api";
import { getData } from "../../../../services/library";
import { useFechas } from "../context/FechaContext";
import { UrlApi } from "../../../../services/apirest";
import {
  AppBar,
  IconButton,
  Toolbar,
  Typography,
  Paper,
  Box,
  Button,
  Badge,
  TextField,
  InputAdornment,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 64px)", // ocupar pantalla completa menos la AppBar
};

const centerDefault = {
  lat: -32.94682,
  lng: -60.63932,
};

const polygonOptions = {
  fillColor: "#2196f3",
  fillOpacity: 0.15,
  strokeColor: "#0d47a1",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  clickable: true,
  editable: false,
  zIndex: 1,
};

function MapaPedidos() {
  let navigate = useNavigate();
  const { desde, hasta } = useFechas();
  const [pedidos, setPedidos] = useState([]);
  const [center, setCenter] = useState(centerDefault);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [zonaSeleccionada, setZonaSeleccionada] = useState(null);
  const [geoZonas, setGeoZonas] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [camionSeleccionado, setCamionSeleccionado] = useState(null);
  const [rendiciones, setRendiciones] = useState([]);
  const [verVehiculos, setVerVehiculos] = useState(false);
  const [busquedaVehiculo, setBusquedaVehiculo] = useState("");
  const googleMapsApiKey = process.env.REACT_APP_Google_maps_key;
  const GpsApi = "https://apigps.netizar.com/api";

  // Cargar geozonas
  const loadGeozonas = async () => {
    try {
      const response = await fetch(`${UrlApi}/geozonas/`);
      if (!response.ok) throw new Error("Error al cargar geozonas");
      const data = await response.json();
      setGeoZonas(data);
    } catch (err) {
      console.error("Error cargando geozonas:", err);
    }
  };

  // Cargar posiciones de camiones (GPS) y refrescarlas cada 30s
  const loadCamiones = async () => {
    try {
      const response = await fetch(`${GpsApi}/camiones/posiciones`);
      if (!response.ok) throw new Error("Error al cargar posiciones de camiones");
      const data = await response.json();
      setCamiones(data);
    } catch (err) {
      console.error("Error cargando camiones:", err);
    }
  };

  // Cargar rendiciones (trae, entre otros datos, el vehiculo -"patente"- que se esta usando)
  const obtenerRendiciones = () => {
    getData(
      "/envasado_rendiciones/sucauth/" +
        desde +
        "/" +
        hasta +
        "/" +
        localStorage.getItem("suc_auth").split(","),
      (data) => {
        setRendiciones(data);
      },
      (error) => {
        console.error("Error al cargar rendiciones:", error);
      },
    );
  };

  // Cargar pedidos
  useEffect(() => {
    loadGeozonas();
    loadCamiones();
    const interval = setInterval(loadCamiones, 30000);
    if (desde && hasta) {
      obtenerRendiciones();
      getData(
        `/envasado_pedidos/mapapedidos/${desde} 00:00:00/${hasta} 23:59:59/${localStorage
          .getItem("suc_auth")
          .split(",")}`,
        (data) => {
          setPedidos(data);
          if (data.length > 0) {
            setCenter({ lat: data[0].latitud, lng: data[0].longitud });
          }
        }
      );
    }
    return () => clearInterval(interval);
  }, [desde, hasta]);

  const handleClose = () => {
    navigate(-1);
  };

  // Normaliza patentes para poder compararlas: mayusculas y solo caracteres alfanumericos
  // (ignora espacios, guiones, puntos u otro formato que varie entre ambas fuentes)
  const normalizarPatente = (valor) =>
    (valor || "").toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

  // Solo los camiones cuya patente (name en tc_devices) figura en alguna rendicion cargada,
  // con los datos de esa rendicion (chofer, estado, sucursal) ya combinados.
  const camionesConRendicion = useMemo(() => {
    return camiones
      .map((camion) => {
        const rendicion = rendiciones.find(
          (r) => normalizarPatente(r.patente) === normalizarPatente(camion.name)
        );
        return rendicion ? { ...camion, rendicion } : null;
      })
      .filter(Boolean);
  }, [camiones, rendiciones]);

  // Filtra la lista del desplegable por patente o nombre del chofer
  const rendicionesFiltradas = useMemo(() => {
    const termino = busquedaVehiculo.trim().toLowerCase();
    if (!termino) return rendiciones;
    return rendiciones.filter(
      (r) =>
        (r.patente || "").toLowerCase().includes(termino) ||
        (r.nombre_apellido || "").toLowerCase().includes(termino)
    );
  }, [rendiciones, busquedaVehiculo]);

  // Al clickear un vehiculo en la lista, centra el mapa en su marker y abre su InfoWindow
  const irAlMarkerDelCamion = (rendicion) => {
    const camion = camionesConRendicion.find(
      (c) => normalizarPatente(c.name) === normalizarPatente(rendicion.patente)
    );
    if (!camion) return;
    setCenter({ lat: camion.latitude, lng: camion.longitude });
    setCamionSeleccionado(camion);
    setPedidoSeleccionado(null);
    setZonaSeleccionada(null);
    setVerVehiculos(false);
  };

const getMarkerIcon = (estadoId) => {
  const base = "http://maps.google.com/mapfiles/ms/icons/";
  switch (estadoId) {
    case 246: return base + "yellow-dot.png";     // #ffff88
    case 251: return base + "lightblue-dot.png";  // #e2e2e2 (no hay gris exacto)
    case 252: return base + "green-dot.png";      // #acec98
    case 253: return base + "blue-dot.png";       // #82ddfc
    case 255: return base + "orange-dot.png";     // #F28B82 (similar)
    default:  return base + "red-dot.png";
  }
};




  return (
    <div>
      {/* Barra superior */}
      <AppBar position="relative" sx={{ bgcolor: "primary.main", boxShadow: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            Mapa de pedidos
          </Typography>
          <Badge
            badgeContent={camionesConRendicion.length}
            color="error"
            sx={{ mr: 2 }}
          >
            <Button
              variant="contained"
              color="warning"
              startIcon={<LocalShippingIcon />}
              onClick={() => setVerVehiculos(true)}
              sx={{ fontWeight: "bold" }}
            >
              Vehículos
            </Button>
          </Badge>
          <IconButton edge="end" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <LoadScript googleMapsApiKey={googleMapsApiKey}>
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={13}>
          {/* Marcadores de pedidos */}
          {pedidos.map((pedido) => (
            <Marker
              key={pedido.id}
              position={{ lat: pedido.latitud, lng: pedido.longitud }}
              onClick={() => {
                setPedidoSeleccionado(pedido);
                setZonaSeleccionada(null);
                setCamionSeleccionado(null);
              }}
              title={pedido.razon_social}
              icon={getMarkerIcon(pedido.estado)}
              
            />
          ))}

          {/* Marcadores de camiones (GPS), filtrados a los que tienen una rendicion cargada */}
          {camionesConRendicion.map((camion) => (
            <Marker
              key={`camion-${camion.id}`}
              position={{ lat: camion.latitude, lng: camion.longitude }}
              onClick={() => {
                setCamionSeleccionado(camion);
                setPedidoSeleccionado(null);
                setZonaSeleccionada(null);
              }}
              title={camion.name}
              icon="http://maps.google.com/mapfiles/ms/icons/truck.png"
            />
          ))}

          {/* Ventana con detalles del camion */}
          {camionSeleccionado && (
            <InfoWindow
              position={{
                lat: camionSeleccionado.latitude,
                lng: camionSeleccionado.longitude,
              }}
              onCloseClick={() => setCamionSeleccionado(null)}
            >
              <Paper elevation={3} sx={{ p: 1.5, maxWidth: 250 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {camionSeleccionado.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Identificador:</strong> {camionSeleccionado.uniqueid}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Velocidad:</strong>{" "}
                  {camionSeleccionado.speed != null
                    ? `${(camionSeleccionado.speed * 1.852).toFixed(1)} km/h`
                    : "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Última actualización:</strong>{" "}
                  {camionSeleccionado.fixtime
                    ? new Date(camionSeleccionado.fixtime).toLocaleString()
                    : "-"}
                </Typography>
                {camionSeleccionado.rendicion && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Chofer:</strong>{" "}
                      {camionSeleccionado.rendicion.nombre_apellido || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Sucursal:</strong>{" "}
                      {camionSeleccionado.rendicion.nombre_suc || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Estado rendición:</strong>{" "}
                      {camionSeleccionado.rendicion.valor || "-"}
                    </Typography>
                  </>
                )}
              </Paper>
            </InfoWindow>
          )}

          {/* Ventana con detalles del pedido */}
          {pedidoSeleccionado && (
            <InfoWindow
              position={{
                lat: pedidoSeleccionado.latitud,
                lng: pedidoSeleccionado.longitud,
              }}
              onCloseClick={() => setPedidoSeleccionado(null)}
            >
              <Paper elevation={3} sx={{ p: 1.5, maxWidth: 250 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {pedidoSeleccionado.razon_social}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Dirección:</strong> {pedidoSeleccionado.direccion_entrega}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fecha para:</strong>{" "}
                  {new Date(pedidoSeleccionado.fecha_para).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Estado:</strong> {pedidoSeleccionado.valor_estado}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Chofer:</strong>{" "}
                  {pedidoSeleccionado.nombre_apellido || "Sin asignar"}
                </Typography>
              </Paper>
            </InfoWindow>
          )}

          {/* Polígonos de zonas */}
          {geoZonas.map((zona) => (
            <Polygon
              key={zona.id}
              paths={zona.coordinates}
              options={polygonOptions}
              onClick={() => {
                setZonaSeleccionada(zona);
                setPedidoSeleccionado(null);
                setCamionSeleccionado(null);
              }}
              onMouseOver={(e) =>
                (e.latLng && (e.overlayOptions = { fillOpacity: 0.3 }))
              }
              onMouseOut={(e) =>
                (e.latLng && (e.overlayOptions = { fillOpacity: 0.15 }))
              }
            />
          ))}

          {/* InfoWindow para la zona */}
          {zonaSeleccionada && zonaSeleccionada.coordinates?.[0] && (
            <InfoWindow
              position={zonaSeleccionada.coordinates[0]}
              onCloseClick={() => setZonaSeleccionada(null)}
            >
              <Paper elevation={3} sx={{ p: 1.5, maxWidth: 220 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {zonaSeleccionada.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {zonaSeleccionada.descripcion}
                </Typography>
              </Paper>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      <Drawer
        anchor="right"
        open={verVehiculos}
        onClose={() => setVerVehiculos(false)}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Vehículos en uso
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Según las rendiciones cargadas ({desde} a {hasta})
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por patente o chofer..."
            value={busquedaVehiculo}
            onChange={(e) => setBusquedaVehiculo(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List dense>
            {(rendiciones ?? []).length === 0 && (
              <ListItem>
                <ListItemText primary="No hay rendiciones cargadas." />
              </ListItem>
            )}
            {rendiciones.length > 0 && rendicionesFiltradas.length === 0 && (
              <ListItem>
                <ListItemText primary="Sin resultados para la búsqueda." />
              </ListItem>
            )}
            {rendicionesFiltradas.map((r, idx) => {
              const tieneGps = camionesConRendicion.some(
                (c) => normalizarPatente(c.name) === normalizarPatente(r.patente)
              );
              return (
                <React.Fragment key={r.id ?? idx}>
                  <ListItemButton
                    disabled={!tieneGps}
                    onClick={() => irAlMarkerDelCamion(r)}
                  >
                    <ListItemText
                      primary={r.patente || "Sin vehículo asignado"}
                      secondary={
                        `${r.nombre_apellido || "-"} · ${r.valor || "-"}` +
                        (tieneGps ? "" : " (sin GPS)")
                      }
                    />
                  </ListItemButton>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </div>
  );
}

export default MapaPedidos;
